import { useEffect, useRef, type ReactNode } from "react"

import "regenerator-runtime/runtime"

import Head from "next/head"
import { useBottomEdgeInset } from "@/hooks/use-bottom-edge-inset"
import { Session } from "@/types/auth"
import { BasicPageProps } from "@/types/page"
import { Honeybadger } from "@honeybadger-io/react"
import { Provider } from "react-redux"

import { subscribeToAuthEvents } from "@/lib/auth/broadcast-channel"
import { sessionRefreshEmitter } from "@/lib/auth/session-refresh-emitter"
import { mtLocation } from "@/lib/mt-location"
import {
  selectOrganization,
  setEmbedded,
  setEnableImpressions,
} from "@/lib/storage/app-slice"
import { updateSessionFromRefresh } from "@/lib/storage/app-thunks"
import { registerCalendarSyncStore } from "@/lib/storage/calendar-sync-runtime"
import { useAppDispatch, useAppSelector } from "@/lib/storage/hooks"
import { AppStore, buildStore } from "@/lib/storage/redux-store"
import { userSlice } from "@/lib/storage/user-slice"
import { RELEASE_VERSION } from "@/lib/version"
import { ActivityMonitor } from "@/components/activity-monitor"
import { AuthDialog } from "@/components/auth/auth-dialog"
import { DialogInfo } from "@/components/dialog-info"
import { EmbedClose } from "@/components/embedded/embed-close"
import { EmbeddedRouteHandler } from "@/components/embedded/embedded-route-handler"
import { Fonts } from "@/components/fonts"
import { MindtripErrorBoundary } from "@/components/mindtrip-error-boundary"
import { ObtrusiveScrollbarTest } from "@/components/obtrusive-scrollbar-test"
import { PresenceDetector } from "@/components/presence-detector"
import { Toaster } from "@/components/ui/toast"
import { TooltipProvider } from "@/components/ui/tooltip"
import { UnhandledErrorComponent } from "@/components/unhandled-error-component"
import { ViewSizeHandler } from "@/components/view-size-handler"

export type EmbeddedAppProps = BasicPageProps

function EmbeddedTitle() {
  const organization = useAppSelector(selectOrganization)
  const title = organization?.name
    ? `${organization.name} | mindtrip`
    : `mindtrip`
  return (
    <Head>
      <title>{title}</title>
    </Head>
  )
}

// component to fire off dispatches on page load
function Bootstrap(props: BasicPageProps) {
  const dispatch = useAppDispatch()
  dispatch(setEmbedded(true))
  dispatch(userSlice.actions.setBrowser(props.browser))
  dispatch(setEnableImpressions(!!props.enableImpressions))

  // Listen for session refresh events to update Redux state
  useEffect(() => {
    const handleSessionRefresh = (session: Session) => {
      void dispatch(updateSessionFromRefresh(session))
    }

    sessionRefreshEmitter.addListener(handleSessionRefresh)

    return () => {
      sessionRefreshEmitter.removeListener(handleSessionRefresh)
    }
  }, [dispatch])

  // Listen for auth events from other tabs (embedded mode)
  useEffect(() => {
    return subscribeToAuthEvents((event) => {
      switch (event.type) {
        case "auth-signin":
        case "auth-register":
        case "auth-create-anon":
          // Reload page when auth state changes
          setTimeout(() => mtLocation.reload(), 100)
          break
        case "auth-signout":
          // For embedded, always reload regardless of type
          setTimeout(() => mtLocation.reload(), 100)
          break
        case "auth-refresh":
          // Update session without reload
          if (event.session) {
            void dispatch(updateSessionFromRefresh(event.session))
          }
          break
        case "session-check":
          // Embedded mode doesn't need to handle session-check
          break
      }
    })
  }, [dispatch])

  return null
}

export default function EmbeddedApp({
  children,
  ...pageProps
}: EmbeddedAppProps & {
  children: ReactNode
}) {
  const storeRef = useRef<AppStore | null>(null)

  if (!storeRef.current) {
    storeRef.current = buildStore()
    registerCalendarSyncStore(storeRef.current)
  }
  const init = useRef(false)

  useEffect(() => {
    if (!init.current) {
      init.current = true
      console.debug("App Version:", RELEASE_VERSION ?? "NOT_SET")
    }
  }, [])

  // Owns --bottom-inset on its readers — see lib/bottom-edge/writer.ts.
  useBottomEdgeInset()

  // try to set this early, will also be set in HBContext with updated values
  useEffect(() => {
    Honeybadger.setContext({
      version: RELEASE_VERSION,
      supported_browser: pageProps.supportedBrowser,
      embedded: true,
    })
  }, [pageProps.supportedBrowser])

  return (
    <>
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1"
        />
      </Head>
      <MindtripErrorBoundary
        honeybadger={Honeybadger}
        ErrorComponent={UnhandledErrorComponent}
      >
        <Provider store={storeRef.current}>
          <EmbeddedTitle />
          <ActivityMonitor />
          <ViewSizeHandler />
          <PresenceDetector />

          {/*TODO Update HB context within the Embedded App*/}
          {/*<HBContext*/}
          {/*  initUser={pageProps.initUser}*/}
          {/*  supportedBrowser={pageProps.supportedBrowser}*/}
          {/*/>*/}
          <Bootstrap {...pageProps} />
          <EmbeddedRouteHandler />
          <ObtrusiveScrollbarTest />
          <Fonts />
          <TooltipProvider>
            {children}
            <EmbedClose />
          </TooltipProvider>
          <Toaster />
          <DialogInfo />
          <AuthDialog />
        </Provider>
      </MindtripErrorBoundary>
    </>
  )
}
